import { FormDataOptions, MasterData } from "src/app/models/models";

type SimulatorRegistrationRow = {
	aocCode?: string;
	code?: string;
	sortOrder?: number;
};

type AircraftPrefixRow = {
	aocCode?: string;
	prefix?: string;
};

export function enrichMasterDataWithAocCatalog(raw: MasterData | null | undefined): MasterData | null | undefined {
	if (!raw) {
		return raw;
	}

	const simulatorRows = (raw as MasterData & { simulatorRegistrations?: SimulatorRegistrationRow[] })
		.simulatorRegistrations;
	const prefixRows = (raw as MasterData & { aircraftRegistrationPrefixes?: AircraftPrefixRow[] })
		.aircraftRegistrationPrefixes;

	const hasSimulatorRows = Array.isArray(simulatorRows);
	const hasPrefixRows = Array.isArray(prefixRows);

	if (!hasSimulatorRows && !hasPrefixRows) {
		return raw;
	}

	return {
		...raw,
		...(hasPrefixRows ? { aircraftRegistrationPrefixesByAoc: buildAircraftPrefixMap(prefixRows) } : {}),
		...(hasSimulatorRows
			? { simulatorRegistrationOptionsByAoc: buildSimulatorOptionsMap(simulatorRows) }
			: {}),
	};
}

export function masterDataNeedsAocCatalogRefresh(raw: MasterData | null | undefined): boolean {
	if (!raw) {
		return true;
	}

	const payload = raw as MasterData & {
		simulatorRegistrations?: unknown;
		aircraftRegistrationPrefixes?: unknown;
	};

	return !Array.isArray(payload.simulatorRegistrations) && !Array.isArray(payload.aircraftRegistrationPrefixes);
}

function buildAircraftPrefixMap(rows: AircraftPrefixRow[]): Record<string, string> {
	const map: Record<string, string> = {};
	for (const row of rows) {
		const code = row.aocCode?.trim().toLowerCase();
		const prefix = row.prefix?.trim();
		if (code && prefix) {
			map[code] = prefix;
		}
	}
	return map;
}

function buildSimulatorOptionsMap(rows: SimulatorRegistrationRow[]): Record<string, FormDataOptions[]> {
	const grouped = new Map<string, { code: string; sortOrder: number }[]>();

	for (const row of rows) {
		const aocCode = row.aocCode?.trim().toLowerCase();
		const code = row.code?.trim();
		if (!aocCode || !code) {
			continue;
		}

		const list = grouped.get(aocCode) ?? [];
		list.push({ code, sortOrder: row.sortOrder ?? 0 });
		grouped.set(aocCode, list);
	}

	const map: Record<string, FormDataOptions[]> = {};
	for (const [aocCode, items] of grouped.entries()) {
		map[aocCode] = items
			.sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
			.map((item) => ({ code: item.code, description: item.code, value: item.code }));
	}

	return map;
}
